import {graphql} from "@/graphql/generated";
import {mutation, query} from "@/graphql/client";
import {
    AInesObjectiveSubject,
    Booking,
    BookingWithdrawalPotentialSuccessStats,
    BookingWithdrawalRequest,
    BookingWithdrawalRequestUnclaimedConsequence
} from "@/graphql/generated/graphql";
import {kapitelDateTimeString} from "@/graphql/kapitelTypes";
import {defineStore} from "pinia";
import datetime from "@/helper/datetime/datetime";
import date from "@/helper/datetime/date";
import {pluralizeHuman} from "@/helper/amountFormatter";
import {useGlobalEmitter} from "@/helper/emitter";

graphql(`
    fragment BookingWithdrawalRequestFragment on BookingWithdrawalRequest {
        id
        status
        created
        deadline
        ifUnclaimed
        message
        booking { id begin until jobsite { id } }
    }
`);

graphql(`
    fragment BookingWithdrawalPotentialSuccessStatsFragment on BookingWithdrawalPotentialSuccessStats {
        instantWithdrawalPossible
        countSeen
        countSuggestees
        booking { id }
    }
`);

export const fetchWithdrawalRequests = async (): Promise<BookingWithdrawalRequest[]> => {
    const result = await query(graphql(`
        query GetWithdrawalRequests {
            withdrawalRequests {
                ...BookingWithdrawalRequestFragment
            }
        }
    `));
    return result?.data?.withdrawalRequests ?? [];
};

export const removeBookingWithdrawalRequest = async (booking: Booking): Promise<boolean> => {
    const result = await mutation(graphql(`
        mutation RemoveBookingWithdrawalRequest($bookingId: Int!) {
            removeBookingWithdrawalRequest(bookingId: $bookingId)
        }
    `), {bookingId: booking.id});

    // WR is somewhat part of day - calendar requests WR information for each day
    useGlobalEmitter().emit('day:mutated', booking.date)
    useGlobalEmitter().emit('objective:dirty', AInesObjectiveSubject.BookingWithdrawalRequest)

    return !!result?.data?.removeBookingWithdrawalRequest;
};

export const fetchBookingWithdrawalPotentialSuccessStats = async (bookingId: number): Promise<BookingWithdrawalPotentialSuccessStats | undefined> => {
    const result = await query(graphql(`
        query GetBookingWithdrawalPotentialSuccessStats($bookingId: Int!) {
            bookingWithdrawalPotentialSuccessStats(bookingId: $bookingId) {
                ...BookingWithdrawalPotentialSuccessStatsFragment
            }
        }
    `), {bookingId});
    return result?.data?.bookingWithdrawalPotentialSuccessStats;
};

/**
 * t_now   <....dt1....> t_booking
 *  t_deadline <..dt2..> t_booking
 *

 * parallel im backend: 12h vor deadline wir notifiziert wenn unclaimed->cancel
 *
 */
export const computeDeadlineForRequestFinish = (booking: Booking, unclaimedConsequence: BookingWithdrawalRequestUnclaimedConsequence): kapitelDateTimeString => {

    const dayDiff = date.differenceInDays(booking.date, date.getToday())
    const hourDiff = datetime.getDateTimeDiffInHours(datetime.getNow(), booking.begin)

    if (unclaimedConsequence === BookingWithdrawalRequestUnclaimedConsequence.Cancel) {

        const short_notice_days = 3

        if (dayDiff > 45) {
            // time to booking > 45d -> wait 1d
            return datetime.addDays(datetime.getNow(), 1)
        } else if (dayDiff >= 14) {
            // time to booking >= 14d -> give 12h; -- from here on: immediately send notifications
            return datetime.addHours(datetime.getNow(), 12)
        } else if (dayDiff >= 3) {
            // time to booking > 3d -> wait only 6h
            return datetime.addHours(datetime.getNow(), 6)
        } else if (hourDiff >= 12) {
            // time to booking > 12h -> wait 1h
            return datetime.addHours(datetime.getNow(), 1)
        } else if (hourDiff > 2) {
            // if the booking start is at least two hours await, wait just 20 minutes
            return datetime.addMinutes(datetime.getNow(), 20)
        } else {
            // if the booking is almost starting or has started already - deadline is missed.
            return datetime.getNow()
        }
    } else {
        if (hourDiff < 2) {
            // if the booking is almost starting or has started already - deadline is missed.
            return datetime.getNow()
        }

        const max_deadline_duration_days = 14

        const deadline_duration_hours = Math.min(
            hourDiff * 0.25,
            max_deadline_duration_days * 24
        )

        return datetime.addHours(datetime.getNow(), deadline_duration_hours)
    }

}

export const setRequestWithdrawalToSeenByWithdrawer = async (requestId: number): Promise<boolean> => {
    const result = await mutation(graphql(`
        mutation SetRequestWithdrawalToSeenByWithdrawer($requestId: Int!) {
            setRequestWithdrawalToSeenByWithdrawer(requestId: $requestId)
        }
    `), {requestId});

    useGlobalEmitter().emit('objective:dirty', AInesObjectiveSubject.BookingWithdrawalRequest)

    return !!result?.data?.setRequestWithdrawalToSeenByWithdrawer;
};

export const createBookingWithdrawalRequest = async (
    booking: Booking,
    ifUnclaimed: BookingWithdrawalRequestUnclaimedConsequence,
    message?: string | null
): Promise<BookingWithdrawalRequest | null> => {

    const result = await mutation(graphql(`
        mutation CreateBookingWithdrawalRequest(
            $bookingId: Int!,
            $ifUnclaimed: BookingWithdrawalRequestUnclaimedConsequence!,
            $deadline: DateTime!,
            $message: String
        ) {
            createBookingWithdrawalRequest(
                bookingId: $bookingId,
                ifUnclaimed: $ifUnclaimed,
                deadline: $deadline,
                message: $message
            ) {
                ...BookingWithdrawalRequestFragment
            }
        }
    `), {
        bookingId: booking.id,
        ifUnclaimed,
        deadline: computeDeadlineForRequestFinish(booking, ifUnclaimed),
        message: message ?? null
    });

    // WR is somewhat part of day - calendar requests WR information for each day
    useGlobalEmitter().emit('day:mutated', booking.date)
    useGlobalEmitter().emit('objective:dirty', AInesObjectiveSubject.BookingWithdrawalRequest)

    return result?.data?.createBookingWithdrawalRequest ?? null;
};

export const useBookingWithdrawalDraftStore = defineStore('bookingWithdrawalDraft', {
    state: () => ({
        lastMessage: undefined as string | undefined,
        lastMode: BookingWithdrawalRequestUnclaimedConsequence.Keep as BookingWithdrawalRequestUnclaimedConsequence
    }),
    actions: {
        setDraft(message: string | undefined, mode: BookingWithdrawalRequestUnclaimedConsequence) {
            this.lastMessage = message
            this.lastMode = mode
        },
        resetDraft() {
            this.lastMessage = undefined
            this.lastMode = BookingWithdrawalRequestUnclaimedConsequence.Keep
        }
    }
})

export const isPending = (withdrawalRequest: BookingWithdrawalRequest) => withdrawalRequest.status === 'PENDING' || withdrawalRequest.status === 'PENDING_PUSH_NOTIFIED'
export const isFinished = (withdrawalRequest: BookingWithdrawalRequest) => withdrawalRequest.status === 'FINISHED_REMOVABLE' || withdrawalRequest.status === 'FINISHED_OUTCOME_TO_BE_SEEN_BY_WITHDRAWER'
export const unclaimedKeep = (withdrawalRequest: BookingWithdrawalRequest) => withdrawalRequest.ifUnclaimed === 'KEEP'

export const getStatusStringFor = (withdrawalRequest: BookingWithdrawalRequest) => {
    if (isPending(withdrawalRequest)) {
        return withdrawalRequest.ifUnclaimed === 'CANCEL' ? 'Dienst abgegeben' : 'Suche nach Ersatzmitarbeitern läuft...'
    } else if (isFinished(withdrawalRequest)) {
        return withdrawalRequest.ifUnclaimed === 'CANCEL' ? 'Dienst storniert' : 'Leider kein Ersatzmitarbeiter gefunden.'
    } else {
        console.error('missing status')
        return ''
    }
}

/**
 * prediction wording
 */
export const getPredictionStringFor = (stats: BookingWithdrawalPotentialSuccessStats) => {
    const count = stats.countSuggestees ?? 0
    if (stats.instantWithdrawalPossible) {
        return 'Es sind Ersatzmitarbeiter verfügbar, die diesen Dienst direkt übernehmen können.'
    } else if (count > 0) {
        return 'Dieser Dienst kann ' + pluralizeHuman(count, 'einem möglichen Ersatzmitarbeiter', 'möglichen Ersatzmitarbeitern') + ' angeboten werden.'
    } else {
        return 'Aktuell gibt es keine freien Ersatzmitarbeiter für diesen Dienst.'
    }
}
export const getPredictionBasedButtonLabel = (stats?: BookingWithdrawalPotentialSuccessStats) => {
    return stats?.instantWithdrawalPossible ? 'Dienst direkt abgeben' : 'Ersatz suchen / Stornieren'
}

export default {
    fetchWithdrawalRequests,
    removeBookingWithdrawalRequest,
    fetchBookingWithdrawalPotentialSuccessStats,
    setRequestWithdrawalToSeenByWithdrawer,
    createBookingWithdrawalRequest,
    useBookingWithdrawalDraftStore,
};
